import { formatWalkingTimeShort } from './distanceFormat';

export const FIND_METERS_TO_FEET = 3.28084;
export const FIND_FEET_PER_MILE = 5280;
export const FIND_QUARTER_MILE_FEET = FIND_FEET_PER_MILE * 0.25;
export const FIND_RIGHT_HERE_LABEL = 'Right here';
/** Past this, even inside the GPS uncertainty, a distance is still worth saying ("about 60 ft"). */
export const FIND_RIGHT_HERE_MAX_FEET = 50;

export type FindDistanceMode = 'right-here' | 'feet' | 'miles';

export type FindDistanceFormatOptions = {
  /** UWB-precise reading — 1 ft steps under 10 ft instead of "Right here". */
  uwbPrecise?: boolean;
  /** Horizontal accuracy floor (meters); display granularity never finer than this. */
  horizontalAccuracyMeters?: number | null;
  /** Per-peer hysteresis key; omit to skip smoothing. */
  peerId?: string | null;
};

export type FindDistanceQuantized = {
  rawFeet: number;
  quantizedFeet: number;
  stepFeet: number;
  mode: FindDistanceMode;
  label: string;
};

type HysteresisState = {
  rawFeet: number;
  quantizedFeet: number;
  stepFeet: number;
  mode: FindDistanceMode;
  label: string;
};

const hysteresisByPeer = new Map<string, HysteresisState>();

export function resetFindDistanceHysteresis(peerId?: string): void {
  if (peerId) {
    hysteresisByPeer.delete(peerId);
    return;
  }
  hysteresisByPeer.clear();
}

/** Drop hysteresis entries for peers no longer visible (e.g. after a group switch). */
export function pruneFindDistanceHysteresis(keepPeerIds: Iterable<string>): void {
  const keep = new Set(keepPeerIds);
  for (const peerId of hysteresisByPeer.keys()) {
    if (!keep.has(peerId)) {
      hysteresisByPeer.delete(peerId);
    }
  }
}

export function metersToFeet(meters: number): number {
  return meters * FIND_METERS_TO_FEET;
}

/** Ladder step size in feet before accuracy floor (not hysteresis). */
export function findDistanceLadderStepFeet(
  feet: number,
  uwbPrecise = false,
): FindDistanceQuantized {
  if (!Number.isFinite(feet) || feet < 0) {
    return {
      rawFeet: 0,
      quantizedFeet: 0,
      stepFeet: 10,
      mode: 'right-here',
      label: '—',
    };
  }

  if (feet < 10) {
    if (uwbPrecise) {
      const quantizedFeet = Math.round(feet);
      return {
        rawFeet: feet,
        quantizedFeet,
        stepFeet: 1,
        mode: 'feet',
        label: formatQuantizedLabel(quantizedFeet, 'feet'),
      };
    }
    return {
      rawFeet: feet,
      quantizedFeet: 0,
      stepFeet: 10,
      mode: 'right-here',
      label: FIND_RIGHT_HERE_LABEL,
    };
  }

  if (feet < 30) {
    return quantizeFeetBand(feet, 5, 'feet');
  }
  if (feet < 100) {
    return quantizeFeetBand(feet, 10, 'feet');
  }
  if (feet < 300) {
    return quantizeFeetBand(feet, 25, 'feet');
  }
  if (feet < 1000) {
    return quantizeFeetBand(feet, 50, 'feet');
  }
  if (feet < FIND_QUARTER_MILE_FEET) {
    return quantizeFeetBand(feet, 100, 'feet');
  }

  const miles = feet / FIND_FEET_PER_MILE;
  const quantizedMiles = Math.round(miles * 10) / 10;
  const quantizedFeet = quantizedMiles * FIND_FEET_PER_MILE;
  return {
    rawFeet: feet,
    quantizedFeet,
    stepFeet: FIND_FEET_PER_MILE * 0.1,
    mode: 'miles',
    label: formatQuantizedLabel(quantizedFeet, 'miles'),
  };
}

function quantizeFeetBand(feet: number, stepFeet: number, mode: 'feet'): FindDistanceQuantized {
  const quantizedFeet = Math.round(feet / stepFeet) * stepFeet;
  return {
    rawFeet: feet,
    quantizedFeet,
    stepFeet,
    mode,
    label: formatQuantizedLabel(quantizedFeet, mode),
  };
}

function formatQuantizedLabel(quantizedFeet: number, mode: FindDistanceMode): string {
  if (mode === 'right-here') {
    return FIND_RIGHT_HERE_LABEL;
  }
  if (mode === 'miles') {
    const miles = quantizedFeet / FIND_FEET_PER_MILE;
    return `${miles.toFixed(1)} mi`;
  }
  return `${Math.round(quantizedFeet)} ft`;
}

/** Apply horizontal-accuracy floor — ladder is minimum coarseness, not maximum. */
export function applyFindDistanceAccuracyFloor(
  target: FindDistanceQuantized,
  horizontalAccuracyMeters: number | null | undefined,
): FindDistanceQuantized {
  if (
    horizontalAccuracyMeters == null ||
    !Number.isFinite(horizontalAccuracyMeters) ||
    horizontalAccuracyMeters <= 0
  ) {
    return target;
  }

  const accuracyFeet = horizontalAccuracyMeters * FIND_METERS_TO_FEET;

  // Closer than the two fixes can tell apart: they are beside you. Lexie read "20 ft" sitting next to Sam
  // at the Greek (2026-10-11) - 5.8 m of pure GPS noise. A number there is a claim we cannot make.
  if (
    target.mode !== 'miles' &&
    target.rawFeet < Math.min(accuracyFeet, FIND_RIGHT_HERE_MAX_FEET)
  ) {
    return {
      rawFeet: target.rawFeet,
      quantizedFeet: 0,
      stepFeet: target.stepFeet,
      mode: 'right-here',
      label: FIND_RIGHT_HERE_LABEL,
    };
  }

  if (target.mode === 'miles') {
    const accuracyMiles = accuracyFeet / FIND_FEET_PER_MILE;
    const mileStep = Math.max(0.1, Math.ceil(accuracyMiles * 10) / 10);
    const miles = target.rawFeet / FIND_FEET_PER_MILE;
    const quantizedMiles = Math.round(miles / mileStep) * mileStep;
    const quantizedFeet = quantizedMiles * FIND_FEET_PER_MILE;
    return {
      rawFeet: target.rawFeet,
      quantizedFeet,
      stepFeet: mileStep * FIND_FEET_PER_MILE,
      mode: 'miles',
      label: `${quantizedMiles.toFixed(1)} mi`,
    };
  }

  const effectiveStep = Math.max(target.stepFeet, accuracyFeet);
  if (target.mode === 'right-here' && effectiveStep >= 10) {
    const quantizedFeet = Math.round(target.rawFeet / effectiveStep) * effectiveStep;
    return {
      rawFeet: target.rawFeet,
      quantizedFeet,
      stepFeet: effectiveStep,
      mode: 'feet',
      label: formatQuantizedLabel(quantizedFeet, 'feet'),
    };
  }

  if (effectiveStep === target.stepFeet) {
    return target;
  }

  const quantizedFeet = Math.round(target.rawFeet / effectiveStep) * effectiveStep;
  return {
    rawFeet: target.rawFeet,
    quantizedFeet,
    stepFeet: effectiveStep,
    mode: 'feet',
    label: formatQuantizedLabel(quantizedFeet, 'feet'),
  };
}

/**
 * The distance under which a reading says "Right here": the ladder's 10 ft, raised by the accuracy floor to what the
 * two fixes cannot tell apart (applyFindDistanceAccuracyFloor), up to FIND_RIGHT_HERE_MAX_FEET.
 */
function rightHereBelowFeet(horizontalAccuracyMeters: number | null | undefined): number {
  const accuracyFeet =
    horizontalAccuracyMeters != null &&
    Number.isFinite(horizontalAccuracyMeters) &&
    horizontalAccuracyMeters > 0
      ? horizontalAccuracyMeters * FIND_METERS_TO_FEET
      : 0;
  return Math.max(10, Math.min(accuracyFeet, FIND_RIGHT_HERE_MAX_FEET));
}

/**
 * Hold a label until the distance is clearly past the next one. Between feet and "Right here" the boundary is where
 * this reading turns into "Right here" (rightHereFeet), with a margin of half a step but no more than a quarter of the
 * boundary. It was a fixed 10 ft less half the old step: with the accuracy floor's ~23 ft steps that is below zero,
 * and a friend who walked up to you stayed "46 ft" (2026-11-01 sim big-group).
 */
function applyHysteresis(
  peerId: string,
  rawFeet: number,
  target: FindDistanceQuantized,
  rightHereFeet = 10,
): string {
  const prev = hysteresisByPeer.get(peerId);
  if (!prev) {
    hysteresisByPeer.set(peerId, {
      rawFeet,
      quantizedFeet: target.quantizedFeet,
      stepFeet: target.stepFeet,
      mode: target.mode,
      label: target.label,
    });
    return target.label;
  }

  const regimeChanged = prev.mode !== target.mode || prev.stepFeet !== target.stepFeet;
  const softRightHereTransition =
    (prev.mode === 'right-here' && target.mode === 'feet') ||
    (prev.mode === 'feet' && target.mode === 'right-here');

  if (regimeChanged && !softRightHereTransition) {
    hysteresisByPeer.set(peerId, {
      rawFeet,
      quantizedFeet: target.quantizedFeet,
      stepFeet: target.stepFeet,
      mode: target.mode,
      label: target.label,
    });
    return target.label;
  }

  if (target.quantizedFeet === prev.quantizedFeet && target.label === prev.label) {
    prev.rawFeet = rawFeet;
    return prev.label;
  }

  const halfStep = prev.stepFeet / 2;

  if (prev.mode === 'right-here' && target.mode === 'feet') {
    const margin = Math.min(target.stepFeet / 2, rightHereFeet / 4);
    if (rawFeet >= rightHereFeet + margin) {
      hysteresisByPeer.set(peerId, {
        rawFeet,
        quantizedFeet: target.quantizedFeet,
        stepFeet: target.stepFeet,
        mode: target.mode,
        label: target.label,
      });
      return target.label;
    }
    prev.rawFeet = rawFeet;
    return prev.label;
  }

  if (prev.mode === 'feet' && target.mode === 'right-here') {
    const margin = Math.min(prev.stepFeet / 2, rightHereFeet / 4);
    if (rawFeet <= rightHereFeet - margin) {
      hysteresisByPeer.set(peerId, {
        rawFeet,
        quantizedFeet: target.quantizedFeet,
        stepFeet: target.stepFeet,
        mode: target.mode,
        label: target.label,
      });
      return target.label;
    }
    prev.rawFeet = rawFeet;
    return prev.label;
  }

  if (target.quantizedFeet > prev.quantizedFeet) {
    const boundary = (prev.quantizedFeet + target.quantizedFeet) / 2;
    if (rawFeet >= boundary + halfStep) {
      hysteresisByPeer.set(peerId, {
        rawFeet,
        quantizedFeet: target.quantizedFeet,
        stepFeet: target.stepFeet,
        mode: target.mode,
        label: target.label,
      });
      return target.label;
    }
    prev.rawFeet = rawFeet;
    return prev.label;
  }

  if (target.quantizedFeet < prev.quantizedFeet) {
    const boundary = (prev.quantizedFeet + target.quantizedFeet) / 2;
    if (rawFeet <= boundary - halfStep) {
      hysteresisByPeer.set(peerId, {
        rawFeet,
        quantizedFeet: target.quantizedFeet,
        stepFeet: target.stepFeet,
        mode: target.mode,
        label: target.label,
      });
      return target.label;
    }
    prev.rawFeet = rawFeet;
    return prev.label;
  }

  prev.rawFeet = rawFeet;
  return prev.label;
}

export function quantizeFindDistance(
  meters: number,
  options: FindDistanceFormatOptions = {},
): FindDistanceQuantized {
  if (!Number.isFinite(meters)) {
    return {
      rawFeet: 0,
      quantizedFeet: 0,
      stepFeet: 10,
      mode: 'right-here',
      label: '—',
    };
  }

  const rawFeet = metersToFeet(meters);
  const ladder = findDistanceLadderStepFeet(rawFeet, options.uwbPrecise === true);
  return applyFindDistanceAccuracyFloor(ladder, options.horizontalAccuracyMeters);
}

/** Human-readable Find distance label with optional per-peer hysteresis. */
export function formatFindDistance(
  meters: number,
  options: FindDistanceFormatOptions = {},
): string {
  const target = quantizeFindDistance(meters, options);
  if (!options.peerId) {
    return target.label;
  }
  return applyHysteresis(
    options.peerId,
    target.rawFeet,
    target,
    rightHereBelowFeet(options.horizontalAccuracyMeters),
  );
}

export function formatFindDistanceWithWalkTime(
  meters: number,
  options: FindDistanceFormatOptions = {},
): string {
  const distanceLabel = formatFindDistance(meters, options);
  if (distanceLabel === '—') {
    return '—';
  }
  return `${distanceLabel} · ${formatWalkingTimeShort(meters)}`;
}

/** Shared Find peer distance + walk time (list rows, radar readout, trackedPeerLabel). */
export function formatFindPeerDistanceWithWalkTime(
  distanceMeters: number,
  options: {
    peerId?: string | null;
    uwbPrecise?: boolean;
    horizontalAccuracyMeters?: number | null;
  } = {},
): string {
  return formatFindDistanceWithWalkTime(distanceMeters, {
    peerId: options.peerId ?? undefined,
    uwbPrecise: options.uwbPrecise === true,
    horizontalAccuracyMeters: options.horizontalAccuracyMeters,
  });
}

/** Combined horizontal uncertainty from self + peer GPS accuracy (meters). */
export function combineHorizontalAccuracyMeters(
  selfAccuracyMeters: number | null | undefined,
  peerAccuracyMeters: number | null | undefined,
): number | null {
  const values = [selfAccuracyMeters, peerAccuracyMeters].filter(
    (value): value is number => value != null && Number.isFinite(value) && value > 0,
  );
  if (values.length === 0) {
    return null;
  }
  if (values.length === 1) {
    return values[0];
  }
  return Math.hypot(values[0], values[1]);
}
